/**
 * Safe Array Utilities
 * Runtime tracing for undefined array operations
 */
// Development mode flag - safely check for import.meta.env
const isDev = (() => {
  try {
    // Check if import.meta exists and has env property
    if (typeof import.meta !== 'undefined' && import.meta.env) {
      return import.meta.env.DEV === true
    }
  } catch (e) {
    // import.meta not available
  }
  // Fallback to process.env
  try {
    return process.env.NODE_ENV !== 'production'
  } catch (e) {
    // Neither available, assume production
    return false
  }
})()
/**
 * Safe forEach wrapper with logging
 */
export function safeForEach<T>(
  label: string,
  arr: T[] | undefined | null,
  fn: (item: T, index: number, array: T[]) => void,
): void {
  if (!Array.isArray(arr)) {
    if (isDev) {
      console.error('====================================')
      console.error('[SAFEFOREACH ERROR] Variable is not an array!')
      console.error('[SAFEFOREACH ERROR] Label:', label)
      console.error('[SAFEFOREACH ERROR] Type:', typeof arr)
      console.error('[SAFEFOREACH ERROR] Value:', arr)
      console.error('[SAFEFOREACH ERROR] Stack trace:')
      console.error(new Error().stack)
      console.error('====================================')
    }
    return
  }
  arr.forEach(fn)
}
/**
 * Safe map wrapper with logging
 */
export function safeMap<T, R>(
  label: string,
  arr: T[] | undefined | null,
  fn: (item: T, index: number, array: T[]) => R,
): R[] {
  if (!Array.isArray(arr)) {
    if (isDev) {
      console.error('====================================')
      console.error('[SAFEMAP ERROR] Variable is not an array!')
      console.error('[SAFEMAP ERROR] Label:', label)
      console.error('[SAFEMAP ERROR] Type:', typeof arr)
      console.error('[SAFEMAP ERROR] Value:', arr)
      console.error('[SAFEMAP ERROR] Stack trace:')
      console.error(new Error().stack)
      console.error('====================================')
    }
    return []
  }
  return arr.map(fn)
}
/**
 * Safe filter wrapper with logging
 */
export function safeFilter<T>(
  label: string,
  arr: T[] | undefined | null,
  fn: (item: T, index: number, array: T[]) => boolean,
): T[] {
  if (!Array.isArray(arr)) {
    if (isDev) {
      console.error('====================================')
      console.error('[SAFEFILTER ERROR] Variable is not an array!')
      console.error('[SAFEFILTER ERROR] Label:', label)
      console.error('[SAFEFILTER ERROR] Type:', typeof arr)
      console.error('[SAFEFILTER ERROR] Value:', arr)
      console.error('[SAFEFILTER ERROR] Stack trace:')
      console.error(new Error().stack)
      console.error('====================================')
    }
    return []
  }
  return arr.filter(fn)
}
/**
 * Ensure value is an array
 */
export function ensureArray<T>(
  label: string,
  value: T[] | undefined | null,
): T[] {
  if (Array.isArray(value)) {
    return value
  }
  if (isDev) {
    console.warn('====================================')
    console.warn('[ensureArray] Converting non-array to empty array')
    console.warn('[ensureArray] Label:', label)
    console.warn('[ensureArray] Type:', typeof value)
    console.warn('[ensureArray] Value:', value)
    console.warn('====================================')
  }
  return []
}
/**
 * Global array operation interceptor for development
 */
export function installArrayInterceptor() {
  if (!isDev) return
  const originalForEach = Array.prototype.forEach
  const originalMap = Array.prototype.map
  const originalFilter = Array.prototype.filter
  // Intercept forEach
  Array.prototype.forEach = function (callback, thisArg) {
    if (this === undefined || this === null) {
      console.error('====================================')
      console.error('[ARRAY INTERCEPTOR] forEach called on undefined/null!')
      console.error('[ARRAY INTERCEPTOR] this:', this)
      console.error('[ARRAY INTERCEPTOR] Stack trace:')
      console.error(new Error().stack)
      console.error('====================================')
      return
    }
    return originalForEach.call(this, callback, thisArg)
  }
  // Intercept map
  Array.prototype.map = function (callback, thisArg) {
    if (this === undefined || this === null) {
      console.error('====================================')
      console.error('[ARRAY INTERCEPTOR] map called on undefined/null!')
      console.error('[ARRAY INTERCEPTOR] this:', this)
      console.error('[ARRAY INTERCEPTOR] Stack trace:')
      console.error(new Error().stack)
      console.error('====================================')
      return []
    }
    return originalMap.call(this, callback, thisArg)
  }
  // Intercept filter
  Array.prototype.filter = function (callback, thisArg) {
    if (this === undefined || this === null) {
      console.error('====================================')
      console.error('[ARRAY INTERCEPTOR] filter called on undefined/null!')
      console.error('[ARRAY INTERCEPTOR] this:', this)
      console.error('[ARRAY INTERCEPTOR] Stack trace:')
      console.error(new Error().stack)
      console.error('====================================')
      return []
    }
    return originalFilter.call(this, callback, thisArg)
  }
  console.log('[Array Interceptor] Installed global array operation monitoring')
}
